import {
  Box,
  DropdownButtonContext,
  FlexBox,
  Icon,
  InputRichText,
  Text,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { memo, useContext } from 'react';
import { useMount } from 'react-use';

import { aiApi, ReadAiWritingImprovementArg } from 'apis/ai';
import { AiLoadingState } from 'components/aiLoadingState';
import { useEditorContext } from 'utils/useEditorContext';

interface EditorCopilotDropdownExpandTextProps {
  channel?: Channel;
  lastMessage: string;
  csid?: string;
}

/** Component that represents the "expand" action in the copilot dropdown. */
export const EditorCopilotDropdownExpandText = memo(
  function EditorCopilotDropdownExpandText({
    channel,
    lastMessage,
    csid,
  }: EditorCopilotDropdownExpandTextProps) {
    const { replace, getSelectedText } = useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const { content } = useContext(InputRichText.Context);
    const [improveReply] = aiApi.useCreateAiWritingImprovementMutation();
    const text = getSelectedText();

    const improveReplyContext: ReadAiWritingImprovementArg['contexts'] = {
      features: 'impr_reply',
      ...(channel && {
        channels: convertChannelCasing(channel, 'snake') as Channel,
      }),
    };

    const performExpand = async () => {
      dropdownContext.closeDropdown();
      content.setUiBlocker(
        <Box style={{ position: 'absolute', bottom: 16 }}>
          <AiLoadingState label="Expanding text" />
        </Box>,
      );

      try {
        const response = await improveReply({
          lastMessage,
          text,
          promptName: 'expand_reply',
          contexts: improveReplyContext,
          ...(csid ? { csid } : {}),
        }).unwrap();
        replace(response.text);
        content.setUiBlocker(null);
      } catch {
        content.setUiBlocker(
          <FlexBox
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              paddingBottom: '30px',
            }}
            alignItems="flex-end"
            justifyContent="center"
            gap="200"
            onClick={() => {
              content.setUiBlocker(null);
            }}
          >
            <Text.Paragraph
              variant="1"
              color="error"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Icon
                name="exclamation"
                color="error"
                style={{ marginRight: '8px' }}
              />
              Failed to apply expand
            </Text.Paragraph>
          </FlexBox>,
        );
      }
    };

    useMount(() => {
      if (text && improveReplyContext) {
        performExpand();
      }
    });

    return null;
  },
);
